import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import useSelectorMemoized from '/business/shared/hooks/useSelectorMemoized';

import { urlApplicationRoot, userPermissions } from '/business/shared/util/Statics';

import getApplicationAuthRuleSelector from '/business/shared/state/selectors/auth/getApplicationAuthRuleSelector';
import getApplicationCarbonCreditServicesEnabledSelector from '/business/shared/state/selectors/application/model/getApplicationCarbonCreditServicesEnabledSelector';

import useAppContext from '/business/shared/hooks/useAppContext';
import useUrlBuilder from '/business/shared/hooks/useUrlBuilder';
import StyledHeaderTabs from '/business/shared/components/widgets/header/__styled__/StyledHeaderTabs';
import StyledHeaderTabsList from '/business/shared/components/widgets/header/__styled__/StyledHeaderTabsList';
import StyledHeaderTabsListItem from '/business/shared/components/widgets/header/__styled__/StyledHeaderTabsListItem';

function HeaderDashboardTabs() {
  const contributionTabViewable =
    useSelectorMemoized(
      (state) => getApplicationAuthRuleSelector(state, userPermissions.CAN_VIEW_OFFER_SETTING),
      [userPermissions.CAN_VIEW_OFFER_SETTING],
    ) || false;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasContribution } = useAppContext();
  const carbonCreditServicesEnabled = useSelectorMemoized(getApplicationCarbonCreditServicesEnabledSelector) || false;
  const { getApplicationSettingsUrl } = useUrlBuilder();

  const settingsTabs = {
    DEALERSHIP_DETAILS: t('business:ApplicationSettings.dealershipDetailsTitle', 'Dealership details'),
    SYS_PREFS: 'System preferences',
    REPORTING_PERIOD: t('business:ApplicationSettings.reportingPeriodTitle', 'Reporting period'),
    USER_SETTINGS: t('business:ApplicationSettings.userSettingsTitle', 'User settings'),
    CONTRIBUTION: t('business:ApplicationSettings.contributionTitle', 'Contribution'),
  };
  const location = useLocation();

  const isApplicationSettingsUrl = location.pathname.startsWith(`/${urlApplicationRoot}-settings`);

  const items = [
    {
      title: settingsTabs.DEALERSHIP_DETAILS,
      testId: 'tab-dealership-details',
      isActive: isApplicationSettingsUrl && location.pathname.includes('/business-details'),
      href: getApplicationSettingsUrl('/business-details'),
    },
    {
      title: settingsTabs.SYS_PREFS,
      testId: 'tab-default-lang',
      isActive: isApplicationSettingsUrl && location.pathname.includes('/sys-prefs'),
      href: getApplicationSettingsUrl('/sys-prefs'),
    },
    {
      title: settingsTabs.REPORTING_PERIOD,
      testId: 'tab-reporting-period',
      isActive: isApplicationSettingsUrl && location.pathname.includes('/reporting-period'),
      href: getApplicationSettingsUrl('/reporting-period'),
    },
    {
      title: settingsTabs.USER_SETTINGS,
      testId: 'tab-user-settings',
      isActive: isApplicationSettingsUrl && location.pathname.includes('/user-settings'),
      href: getApplicationSettingsUrl('/user-settings'),
    },
  ];

  if (hasContribution && contributionTabViewable && carbonCreditServicesEnabled) {
    items.push({
      title: settingsTabs.CONTRIBUTION,
      testId: 'tab-contrib',
      isActive: isApplicationSettingsUrl && location.pathname.includes('/contribution'),
      href: getApplicationSettingsUrl('/contribution'),
    });
  }
  const currentUrl = `${location.pathname}${location.search}`;
  return (
    <StyledHeaderTabs>
      <StyledHeaderTabsList>
        {items.map((item) => (
          <StyledHeaderTabsListItem
            key={item.title}
            $isSettingsListItemStyle
            data-test-id={item.testId}
            $isActive={item.isActive}
            onClick={() => {
              if (item.href && currentUrl !== item.href) {
                navigate(item.href);
              }
            }}
          >
            <div>{item.title}</div>
          </StyledHeaderTabsListItem>
        ))}
      </StyledHeaderTabsList>
    </StyledHeaderTabs>
  );
}

export default HeaderDashboardTabs;
